/**
 * Image Optimization Service - Phase 4b: Progressive Image Loading
 *
 * Handles:
 * - Dominant color extraction for placeholder backgrounds
 * - LQIP (Low Quality Image Placeholder) generation
 * - Image metadata caching
 * - Responsive image srcSet generation
 */

import { logger } from '../utils/logger';
import { advancedCache } from './advancedCache';

interface ImageMetadata {
  imageUrl: string;
  thumbnailUrl?: string;
  dominantColor: string;
  width: number;
  height: number;
  aspectRatio: number;
  cachedAt: number;
}

class ImageOptimizationService {
  private imageMetadataCache = new Map<string, ImageMetadata>();

  /**
   * Extract dominant color from image using Canvas API
   * Returns hex color string
   */
  async extractDominantColor(imageUrl: string): Promise<string> {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = 10; // Small size for performance
            canvas.height = 10;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve('#cccccc'); // Default fallback
              return;
            }

            ctx.drawImage(img, 0, 0, 10, 10);
            const imageData = ctx.getImageData(0, 0, 10, 10);
            const data = imageData.data;

            // Calculate average color
            let r = 0, g = 0, b = 0;
            for (let i = 0; i < data.length; i += 4) {
              r += data[i];
              g += data[i + 1];
              b += data[i + 2];
            }

            const pixelCount = data.length / 4;
            r = Math.round(r / pixelCount);
            g = Math.round(g / pixelCount);
            b = Math.round(b / pixelCount);

            // Convert to hex
            const hex = '#' + [r, g, b]
              .map(x => {
                const hex = x.toString(16);
                return hex.length === 1 ? '0' + hex : hex;
              })
              .join('')
              .toUpperCase();

            resolve(hex);
          } catch (error) {
            logger.error('Error extracting color:', error);
            resolve('#cccccc');
          }
        };

        img.onerror = () => {
          logger.warn(`Failed to load image for color extraction: ${imageUrl}`);
          resolve('#cccccc');
        };

        img.src = imageUrl;
      } catch (error) {
        logger.error('Error in extractDominantColor:', error);
        resolve('#cccccc');
      }
    });
  }

  /**
   * Generate thumbnail URL from full image URL
   * Assumes Supabase storage pattern: URL ends with filename.jpg
   * Returns URL with -thumb suffix before extension
   */
  generateThumbnailUrl(imageUrl: string): string {
    if (!imageUrl) return '';

    // For Supabase URLs: change filename.jpg -> filename-thumb.jpg
    return imageUrl.replace(/\.(jpg|jpeg|png)$/i, '-thumb.$1');
  }

  /**
   * Get or create image metadata
   */
  async getImageMetadata(
    imageUrl: string,
    width: number = 1536,
    height: number = 1024
  ): Promise<ImageMetadata> {
    // Check memory cache first
    if (this.imageMetadataCache.has(imageUrl)) {
      return this.imageMetadataCache.get(imageUrl)!;
    }

    // Check IndexedDB cache
    const cached = await advancedCache.get<ImageMetadata>(`img_meta_${imageUrl}`);
    if (cached) {
      this.imageMetadataCache.set(imageUrl, cached);
      return cached;
    }

    // Generate new metadata
    const dominantColor = await this.extractDominantColor(imageUrl);
    const thumbnailUrl = this.generateThumbnailUrl(imageUrl);
    const aspectRatio = width / height;

    const metadata: ImageMetadata = {
      imageUrl,
      thumbnailUrl,
      dominantColor,
      width,
      height,
      aspectRatio,
      cachedAt: Date.now()
    };

    // Cache in memory and IndexedDB
    this.imageMetadataCache.set(imageUrl, metadata);
    await advancedCache.set(`img_meta_${imageUrl}`, metadata, 'default');

    logger.debug(`Generated image metadata for ${imageUrl}:`, {
      dominantColor,
      thumbnailUrl
    });

    return metadata;
  }

  /**
   * Generate responsive srcSet for image
   * Example: "thumbnail.jpg 30w, medium.jpg 768w, full.jpg 1536w"
   */
  generateSrcSet(
    imageUrl: string,
    thumbnailUrl?: string
  ): string {
    const variants: [string, number][] = [];

    if (thumbnailUrl) {
      variants.push([thumbnailUrl, 30]);
    }

    variants.push([imageUrl, 1536]); // Full resolution

    return variants
      .map(([url, width]) => `${url} ${width}w`)
      .join(', ');
  }

  /**
   * Generate sizes attribute for responsive images
   * Covers mobile, tablet, desktop breakpoints
   */
  generateSizes(): string {
    return '(max-width: 375px) 100vw, (max-width: 768px) 90vw, 1536px';
  }

  /**
   * Batch process multiple images
   */
  async processImages(
    imageUrls: string[],
    width?: number,
    height?: number
  ): Promise<Map<string, ImageMetadata>> {
    const results = new Map<string, ImageMetadata>();

    for (const url of imageUrls) {
      try {
        const metadata = await this.getImageMetadata(url, width, height);
        results.set(url, metadata);
      } catch (error) {
        logger.error(`Error processing image ${url}:`, error);
      }
    }

    return results;
  }

  /**
   * Clear image metadata cache
   */
  clearCache(): void {
    this.imageMetadataCache.clear();
    logger.debug('Cleared image metadata cache');
  }
}

// Export singleton instance
export const imageOptimization = new ImageOptimizationService();
